<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';

const { t } = useI18n();
const days = ref(null);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { contact_purge_after_days } =
      currentAccount.value?.settings || {};

    days.value = contact_purge_after_days;
    isEnabled.value = !!contact_purge_after_days;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  const value = parseInt(days.value, 10);
  if (!value || value < 1 || value > 365) {
    useAlert(t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.DAYS.ERROR'));
    return Promise.resolve();
  }

  return updateAccountSettings({
    contact_purge_after_days: value,
  });
};

const handleDisable = async () => {
  days.value = null;

  return updateAccountSettings({
    contact_purge_after_days: null,
  });
};

const toggleContactPurge = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.NOTE')"
    :hide-content="!isEnabled"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch v-model="isEnabled" @change="toggleContactPurge" />
      </div>
    </template>

    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.DAYS.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.DAYS.HELP')"
      >
        <NextInput
          v-model="days"
          type="number"
          min="1"
          max="365"
          class="w-full"
          :placeholder="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.DAYS.PLACEHOLDER')"
        />
      </WithLabel>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.CONTACT_PURGE.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
